/**
 * B5 heat shimmer — localized chassis-side refraction lens.
 *
 * A full-screen FBO heat-shimmer pass is the textbook approach, but
 * the engine doesn't need one to read tactically: we just want the
 * silhouette of an overheated mech to wobble. A `MeshPhysicalMaterial`
 * with `transmission` set high + a thin torus around the chassis
 * gives a convincing refraction lens that distorts what's behind the
 * chassis without any FBO plumbing.
 *
 * The vertex shader perturbs positions with low-frequency noise so
 * the lens *breathes*, selling it as "rising air" rather than a
 * static glass tube. Heat ramp drives both the strength (uPerturb)
 * and the visibility (mesh.visible).
 */

import { useEffect, useMemo, useRef } from "react";
import { type Mesh, MeshPhysicalMaterial } from "three";
import { useFrame } from "@react-three/fiber";

const SHIMMER_LOCAL_Y = 0.65;
const SHIMMER_RADIUS_OUTER = 0.6;
const SHIMMER_RADIUS_INNER = 0.025; // tube radius — thin glass ring

interface HeatShimmerProps {
  /** Heat ramp 0..1. Below ~0.4 the shimmer hides; above scales smoothly. */
  intensity: number;
}

/**
 * Patch a MeshPhysicalMaterial with a low-freq position perturbation
 * driven by `uTime` + `uStrength` uniforms. Authoring this in
 * `onBeforeCompile` lets us keep the transmission/ior/thickness PBR
 * code intact (which is doing the actual refraction work).
 */
function patchShimmerMaterial(material: MeshPhysicalMaterial): {
  uTime: { value: number };
  uStrength: { value: number };
} {
  const uTime = { value: 0 };
  const uStrength = { value: 0 };
  material.onBeforeCompile = (shader) => {
    shader.uniforms.uTime = uTime;
    shader.uniforms.uStrength = uStrength;
    shader.vertexShader = shader.vertexShader
      .replace(
        "#include <common>",
        `#include <common>
uniform float uTime;
uniform float uStrength;
float shimmerHash(vec3 p) {
  return fract(sin(dot(p, vec3(12.9898, 78.233, 37.719))) * 43758.5453);
}
`,
      )
      .replace(
        "#include <begin_vertex>",
        `#include <begin_vertex>
{
  // Low-frequency noise sweep — perturb XZ radial component, drive a
  // gentle vertical scroll so the bands rise upward.
  float band = position.y * 4.0 + uTime * 1.8;
  float n = shimmerHash(vec3(position.x, floor(band), position.z));
  float wobble = (n - 0.5) * 0.06 * uStrength;
  transformed.x += wobble * sign(position.x);
  transformed.z += wobble * sign(position.z);
}
`,
      );
  };
  material.needsUpdate = true;
  return { uTime, uStrength };
}

export function HeatShimmer({ intensity }: HeatShimmerProps) {
  const ref = useRef<Mesh>(null);
  const material = useMemo(() => {
    const m = new MeshPhysicalMaterial({
      // Transmission-heavy glass for the refraction effect.
      transmission: 0.95,
      thickness: 0.3,
      ior: 1.03,
      roughness: 0.15,
      metalness: 0,
      transparent: true,
      opacity: 0.7,
    });
    return m;
  }, []);
  const uniformsRef = useRef<ReturnType<typeof patchShimmerMaterial> | null>(null);

  useEffect(() => {
    uniformsRef.current = patchShimmerMaterial(material);
    return () => {
      material.dispose();
    };
  }, [material]);

  useFrame((_, dt) => {
    const m = ref.current;
    if (!m) return;
    // Visibility threshold: don't render shimmer below 0.3 intensity
    // (the lens at low strength is just expensive transparency).
    const visible = intensity > 0.3;
    m.visible = visible;
    if (!visible) return;
    if (!uniformsRef.current) return;
    uniformsRef.current.uTime.value += dt;
    uniformsRef.current.uStrength.value = intensity;
    // Subtle vertical scale tied to heat — the lens grows a bit as
    // the chassis runs hotter.
    const sy = 1 + (intensity - 0.3) * 0.25;
    m.scale.set(1, sy, 1);
  });

  return (
    <mesh ref={ref} position={[0, SHIMMER_LOCAL_Y, 0]} material={material} visible={false}>
      <torusGeometry
        args={[SHIMMER_RADIUS_OUTER, SHIMMER_RADIUS_INNER, 12, 48]}
      />
    </mesh>
  );
}
